/* eslint-disable max-len */
// src/components/settings/Settings.js
// Tabbed settings layout: Account, Connections, Team, Notifications

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  doc, getDoc, onSnapshot,
  collection, query, serverTimestamp, setDoc
} from "firebase/firestore";
import { updateProfile } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { db, auth, functions } from '../../firebaseConfig';
import { useAuthUser } from '../../hooks/useAuthUser';
import { FaFacebook, FaLinkedin, FaTwitter, FaYoutube, FaTiktok, FaPinterest } from 'react-icons/fa';
import { FiBell, FiUser, FiGlobe, FiPower, FiSave, FiLoader, FiClock, FiUsers } from 'react-icons/fi';
import { useConfirmDialog } from '../shared/contexts/ConfirmContext';
import { useSearchParams } from 'react-router-dom';
import MetaConsentModal from './MetaConsentModal';
import TeamManagement from './TeamManagement';

const initialConnectionStatus = {
  meta: 'checking',
  linkedin: 'checking',
};

const TABS = [
  { id: 'account', label: 'Account', icon: FiUser },
  { id: 'connections', label: 'Connections', icon: FiGlobe },
  { id: 'team', label: 'Team', icon: FiUsers },
  { id: 'notifications', label: 'Notifications', icon: FiBell },
];

const NOTIFICATION_TYPES = [
  { key: 'post_published', label: 'Post Published', description: 'When a scheduled post goes live' },
  { key: 'post_failed', label: 'Post Failed', description: 'When a post fails to publish' },
  { key: 'stream_complete', label: 'New Batch Generated', description: 'When a content stream creates new posts' },
  { key: 'stream_error', label: 'Stream Error', description: 'When a content stream fails' },
  { key: 'visual_ready', label: 'Visuals Ready', description: 'When visual generation completes' },
  { key: 'visual_failed', label: 'Visual Generation Failed', description: 'When visual generation fails' },
  { key: 'meta_account', label: 'Meta Account Changes', description: 'When a Meta account is linked or unlinked' },
  { key: 'linkedin_account', label: 'LinkedIn Account Changes', description: 'When a LinkedIn account is linked or unlinked' },
  { key: 'inactivity_reminder', label: 'Inactivity Reminders', description: 'Get a nudge if you haven\'t visited in a while', emailOnly: true },
];

const DEFAULT_NOTIFICATION_PREFS = Object.fromEntries(NOTIFICATION_TYPES.filter(t => !t.emailOnly).map(t => [t.key, false]));
const DEFAULT_EMAIL_PREFS = Object.fromEntries(NOTIFICATION_TYPES.map(t => [t.key, false]));

function Settings({ onLogout }) {
  const { confirm } = useConfirmDialog();
  const [searchParams, setSearchParams] = useSearchParams();

  // --- Tab state from URL ---
  const activeTab = useMemo(() => {
    const tab = searchParams.get('tab');
    return TABS.find(t => t.id === tab) ? tab : 'account';
  }, [searchParams]);

  const setActiveTab = useCallback((tabId) => {
    setSearchParams({ tab: tabId }, { replace: true });
  }, [setSearchParams]);

  // --- State Variables ---
  const [userName, setUserName] = useState('');
  const [timeZone, setTimeZone] = useState('');
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState('');
  const [settingsError, setSettingsError] = useState('');
  const [connectionStatus, setConnectionStatus] = useState(initialConnectionStatus);
  const [isLoadingConnections, setIsLoadingConnections] = useState(true);
  const [connectionError, setConnectionError] = useState('');
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(false);
  const [notificationPrefs, setNotificationPrefs] = useState(DEFAULT_NOTIFICATION_PREFS);
  const [emailPrefs, setEmailPrefs] = useState(DEFAULT_EMAIL_PREFS);
  const [savingNotifPref, setSavingNotifPref] = useState(null);
  const [savingEmailPref, setSavingEmailPref] = useState(null);
  const [showMetaConsent, setShowMetaConsent] = useState(false);
  const [userData, setUserData] = useState(null);
  const oauthRedirectProcessedRef = useRef(false);
  const connectionErrorRef = useRef(connectionError);

  // --- Auth State ---
  const { userId, user } = useAuthUser();

  // --- Sync connectionError to ref ---
  useEffect(() => {
    connectionErrorRef.current = connectionError;
  }, [connectionError]);

  // --- Fetch User Data ---
  const fetchUserData = useCallback(async (currentUserId) => {
    if (!currentUserId) {
        setLoadingSettings(false);
        setIsLoadingConnections(false);
        setConnectionStatus({ meta: 'disconnected', linkedin: 'disconnected' });
        return () => {};
    }
    setLoadingSettings(true);
    setIsLoadingConnections(true);
    setSettingsError('');
    setConnectionError('');

    // Fetch user settings doc
    const settingsDocRef = doc(db, "users", currentUserId);
    const settingsPromise = getDoc(settingsDocRef).then(docSnap => {
        if (docSnap.exists()) {
            const data = docSnap.data();
            setUserData(data);
            setUserName(data.name || auth.currentUser?.displayName || '');
            setTimeZone(data.settings?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
            setNotificationsEnabled(data.settings?.notificationsEnabled === true);
            setEmailNotificationsEnabled(data.settings?.emailNotificationsEnabled === true);
            if (data.settings?.notificationPreferences) {
                setNotificationPrefs(prev => ({ ...prev, ...data.settings.notificationPreferences }));
            }
            if (data.settings?.emailPreferences) {
                setEmailPrefs(prev => ({ ...prev, ...data.settings.emailPreferences }));
            }
        } else {
            setUserName(auth.currentUser?.displayName || '');
            setTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
        }
    }).catch(error => {
        setSettingsError(error.code === 'permission-denied' ? "Permissions error fetching settings." : "Could not load preferences.");
    }).finally(() => {
        setLoadingSettings(false);
    });

    // Fetch connections using onSnapshot
    const connectionsQuery = query(collection(db, "users", currentUserId, "connections"));

    const unsubscribeConnections = onSnapshot(connectionsQuery, (querySnapshot) => {
        const newStatus = { meta: 'disconnected', linkedin: 'disconnected' };

        querySnapshot.forEach((connDoc) => {
            const data = connDoc.data();
            if (data.provider === 'meta' || connDoc.id.startsWith('meta_')) {
                newStatus.meta = 'connected';
            }
            if (data.provider === 'linkedin' || connDoc.id.startsWith('linkedin_')) {
                newStatus.linkedin = 'connected';
            }
        });

        // --- Process OAuth Redirect Params ---
        const params = new URLSearchParams(window.location.search);
        const oauthStatus = params.get('oauth');
        const oauthError = params.get('error');
        const metaStatusParam = params.get('status');

        if (oauthStatus && !oauthRedirectProcessedRef.current) {
            if (oauthStatus === 'meta_success' && newStatus.meta !== 'connected') {
                 if (metaStatusParam === 'no_pages') {
                     setConnectionError("Connected to Meta, but no usable Pages/IG Accounts found. Ensure permissions are granted.");
                 } else {
                     setConnectionError('');
                 }
                 oauthRedirectProcessedRef.current = true;
            } else if (oauthStatus === 'meta_failure') {
                 if (oauthError && (oauthError.toLowerCase().includes('not a tester') || oauthError === 'access_denied')) {
                     setConnectionError("Your Facebook access is being set up. You'll receive an email with instructions when it's ready.");
                 } else {
                     setConnectionError(`Meta connection failed: ${oauthError || 'Unknown reason'}`);
                 }
                 newStatus.meta = 'error';
                 oauthRedirectProcessedRef.current = true;
            } else if (oauthStatus === 'linkedin_success' && newStatus.linkedin !== 'connected') {
                 setConnectionError('');
                 oauthRedirectProcessedRef.current = true;
            } else if (oauthStatus === 'linkedin_failure') {
                 setConnectionError(`LinkedIn connection failed: ${oauthError || 'Unknown reason'}`);
                 newStatus.linkedin = 'error';
                 oauthRedirectProcessedRef.current = true;
            }
        }

        if (oauthRedirectProcessedRef.current || (oauthStatus && (newStatus.meta === 'connected' || newStatus.linkedin === 'connected'))) {
             if (window.history.replaceState) {
                 window.history.replaceState({}, document.title, window.location.pathname);
             }
        }

        if ((newStatus.meta === 'connected' && connectionErrorRef.current.includes('Meta connection failed')) ||
            (newStatus.linkedin === 'connected' && connectionErrorRef.current.includes('LinkedIn connection failed'))) {
           setConnectionError('');
        }

        setConnectionStatus(newStatus);
        setIsLoadingConnections(false);

    }, (err) => {
        setConnectionError("Error fetching connection status.");
        setConnectionStatus({ meta: 'error', linkedin: 'error' });
        setIsLoadingConnections(false);
    });

    await settingsPromise;
    return unsubscribeConnections;

  }, []);


  // --- Main useEffect Hook ---
  useEffect(() => {
    const unsubscribeRef = { current: () => {} };
    if (userId) {
        const unsubPromise = fetchUserData(userId);
        unsubPromise.then(unsubFunc => {
            unsubscribeRef.current = unsubFunc;
        }).catch(err => {
            setLoadingSettings(false);
            setIsLoadingConnections(false);
            setSettingsError("Failed to initialize settings.");
        });
    } else {
        setLoadingSettings(false);
        setIsLoadingConnections(false);
        setConnectionStatus({ meta: 'disconnected', linkedin: 'disconnected' });
        setUserName('');
        setTimeZone('');
    }
    return () => {
        unsubscribeRef.current();
    };
  }, [userId, fetchUserData]);


  // --- Handlers ---
  const handleSavePreferences = async () => {
     if (!userId || !userName.trim()) { setSettingsError("Name cannot be empty."); return; }
     setSavingSettings(true); setSettingsMessage(''); setSettingsError('');
     const userDocRef = doc(db, "users", userId);
     if (!auth.currentUser || auth.currentUser.uid !== userId) { setSettingsError("Auth error."); setSavingSettings(false); return; }
     try {
         const dataToSave = {
            userId: userId,
            email: user?.email,
            name: userName.trim(),
            updatedAt: serverTimestamp(),
            settings: {
                timezone: timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
                notificationPreferences: notificationPrefs,
            }
         };
         await setDoc(userDocRef, dataToSave, { merge: true });
         if (auth.currentUser && auth.currentUser.displayName !== userName.trim()) { await updateProfile(auth.currentUser, { displayName: userName.trim() }); }
         setSettingsMessage('Preferences saved!');
     } catch (error) { setSettingsError(error.code === 'permission-denied' ? "Save failed: Permissions error." : "Save failed. Please try again."); }
     finally { setSavingSettings(false); setTimeout(() => setSettingsMessage(''), 3000); }
  };

  const handleMetaConnect = async () => {
      setIsLoadingConnections(true); setConnectionError('');
      try {
        const startAuth = httpsCallable(functions, 'startMetaAuth');
        const result = await startAuth({ frontendOrigin: window.location.origin });
        if (result.data.success && result.data.authorizationUri) { window.location.href = result.data.authorizationUri; }
        else { throw new Error(result.data.error || "Failed to get auth URL."); }
      } catch (err) { setConnectionError("Error starting Meta connection. Please try again."); setIsLoadingConnections(false); }
  };

  const handleMetaDisconnect = async () => {
      const confirmed = await confirm({ title: 'Disconnect Meta', message: 'Disconnect Meta? This will remove the stored connection but does not revoke permissions on Meta\'s side.', confirmLabel: 'Disconnect', variant: 'danger' });
      if (!confirmed) return;
      setIsLoadingConnections(true);
      setConnectionError('');
      try {
          const disconnectFn = httpsCallable(functions, 'disconnectMeta');
          await disconnectFn();
          setConnectionStatus(prevStatus => ({
              ...prevStatus,
              meta: 'disconnected'
          }));
      } catch (err) {
          setConnectionError("Error disconnecting Meta. Please try again.");
      }
      finally {
          setIsLoadingConnections(false);
      }
  };

  const handleLinkedInConnect = async () => {
      setIsLoadingConnections(true); setConnectionError('');
      try {
        const startAuth = httpsCallable(functions, 'startLinkedInAuth');
        const result = await startAuth({ frontendOrigin: window.location.origin });
        if (result.data.success && result.data.authorizationUri) { window.location.href = result.data.authorizationUri; }
        else { throw new Error(result.data.error || "Failed to get auth URL."); }
      } catch (err) { setConnectionError("Error starting LinkedIn connection. Please try again."); setIsLoadingConnections(false); }
  };

  const handleLinkedInDisconnect = async () => {
      const confirmed = await confirm({ title: 'Disconnect LinkedIn', message: 'Disconnect LinkedIn? This will remove the stored connection and unlink all associated projects.', confirmLabel: 'Disconnect', variant: 'danger' });
      if (!confirmed) return;
      setIsLoadingConnections(true);
      setConnectionError('');
      try {
          const disconnectFn = httpsCallable(functions, 'disconnectLinkedIn');
          await disconnectFn();
          setConnectionStatus(prevStatus => ({
              ...prevStatus,
              linkedin: 'disconnected'
          }));
      } catch (err) {
          setConnectionError("Error disconnecting LinkedIn. Please try again.");
      }
      finally {
          setIsLoadingConnections(false);
      }
  };

  const handleToggleNotification = async (key) => {
      const newValue = !notificationPrefs[key];
      const updated = { ...notificationPrefs, [key]: newValue };
      setNotificationPrefs(updated);
      setSavingNotifPref(key);
      try {
          const userDocRef = doc(db, "users", userId);
          await setDoc(userDocRef, { userId, settings: { notificationPreferences: { [key]: newValue } } }, { merge: true });
      } catch (err) {
          setNotificationPrefs(prev => ({ ...prev, [key]: !newValue }));
      } finally {
          setSavingNotifPref(null);
      }
  };

  const handleToggleEmailPref = async (key) => {
      const newValue = !emailPrefs[key];
      const updated = { ...emailPrefs, [key]: newValue };
      setEmailPrefs(updated);
      setSavingEmailPref(key);
      try {
          const userDocRef = doc(db, "users", userId);
          await setDoc(userDocRef, { userId, settings: { emailPreferences: { [key]: newValue } } }, { merge: true });
      } catch (err) {
          setEmailPrefs(prev => ({ ...prev, [key]: !newValue }));
      } finally {
          setSavingEmailPref(null);
      }
  };

  const handleToggleNotificationsMaster = async () => {
      const newValue = !notificationsEnabled;
      setNotificationsEnabled(newValue);
      try {
          const userDocRef = doc(db, "users", userId);
          await setDoc(userDocRef, { userId, settings: { notificationsEnabled: newValue } }, { merge: true });
      } catch (err) {
          setNotificationsEnabled(!newValue);
      }
  };

  const handleToggleEmailMaster = async () => {
      const newValue = !emailNotificationsEnabled;
      setEmailNotificationsEnabled(newValue);
      try {
          const userDocRef = doc(db, "users", userId);
          await setDoc(userDocRef, { userId, settings: { emailNotificationsEnabled: newValue } }, { merge: true });
      } catch (err) {
          setEmailNotificationsEnabled(!newValue);
      }
  };

  // --- Render helpers ---
  const timezones = [
      "UTC",
      "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
      "America/Sao_Paulo", "America/Mexico_City", "America/Toronto",
      "Europe/London", "Europe/Paris", "Europe/Berlin", "Europe/Madrid",
      "Europe/Rome", "Europe/Amsterdam", "Europe/Warsaw", "Europe/Moscow",
      "Asia/Dubai", "Asia/Kolkata", "Asia/Bangkok", "Asia/Singapore",
      "Asia/Shanghai", "Asia/Tokyo", "Asia/Seoul",
      "Australia/Sydney", "Australia/Melbourne", "Pacific/Auckland",
      "Africa/Cairo", "Africa/Lagos", "Africa/Johannesburg",
  ];
  const currentTimeZone = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  if (!timezones.includes(currentTimeZone)) {
      timezones.unshift(currentTimeZone);
  }

  const renderConnection = (platformKey, platformName, icon, comingSoon = false, handlers = null) => {
     const status = connectionStatus[platformKey];
     const isLoading = isLoadingConnections;
     const onConnect = handlers?.onConnect || (() => setShowMetaConsent(true));
     const onDisconnect = handlers?.onDisconnect || handleMetaDisconnect;
     return (
       <div className={`flex flex-col sm:flex-row items-start sm:items-center justify-between py-4 border-b border-white/5 last:border-b-0 ${comingSoon ? 'opacity-50' : ''}`}>
         <div className="flex items-center mb-2 sm:mb-0">
           <span className={`mr-3 text-xl ${comingSoon ? 'text-cream/20' : 'text-slate-blue/60'}`}>{icon}</span>
           <span className={`font-medium ${comingSoon ? 'text-cream/40' : 'text-cream'}`}>{platformName}</span>
         </div>
         <div className="flex items-center space-x-3 w-full sm:w-auto justify-end">
           {comingSoon ? (
             <span className="text-[10px] font-bold text-cream/30 bg-white/5 px-3 py-1 rounded-full uppercase tracking-wider flex items-center gap-1.5">
               <FiClock className="h-3 w-3" /> Coming Soon
             </span>
           ) : (
             <>
               {isLoading && status === 'checking' && <span className="text-xs text-cream/40 italic">Checking...</span>}
               {(!isLoading || status !== 'checking') && status === 'connected' && <span className="text-xs text-pastel-green font-bold bg-green-400/10 px-3 py-1 rounded-full border border-green-400/20">Connected</span>}
               {(!isLoading || status !== 'checking') && (status === 'disconnected' || status === 'no_pages') && <span className="text-xs text-cream/30 font-medium bg-white/5 px-3 py-1 rounded-full">Not Connected</span>}
               {(!isLoading || status !== 'checking') && status === 'error' && <span className="text-xs text-soft-coral font-bold bg-soft-coral/10 px-3 py-1 rounded-full border border-soft-coral/20">Error</span>}
               {(!isLoading || status !== 'checking') && status === 'connected' && (
                 <button onClick={onDisconnect} disabled={isLoading} className="text-xs font-medium text-soft-coral hover:text-red-400 transition-colors disabled:opacity-50">Disconnect</button>
               )}
               {(!isLoading || status !== 'checking') && (status === 'disconnected' || status === 'error' || status === 'no_pages') && (
                 <button onClick={onConnect} disabled={isLoading} className="text-xs font-bold text-slate-blue bg-slate-blue/10 hover:bg-slate-blue/20 px-4 py-1.5 rounded-full transition-all shadow-glow disabled:opacity-50">{isLoading ? '...' : 'Connect'}</button>
               )}
             </>
           )}
         </div>
       </div>
     );
   };

  // --- Tab content renderers ---
  const renderAccountTab = () => (
    <section className="p-8 md:p-10 bg-warm-gray/10 backdrop-blur-md rounded-[3rem] border border-white/5 shadow-card space-y-6">
       <h2 className="text-2xl font-light text-slate-blue flex items-center border-b border-white/5 pb-4 mb-6"><FiUser className="mr-3 text-slate-blue/60"/> User Preferences</h2>
        {settingsError && <p className="text-sm text-soft-coral bg-soft-coral/10 p-4 rounded-2xl border border-soft-coral/20 -mt-2 mb-4 font-medium">{settingsError}</p>}

       <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
           <div className="space-y-2">
               <label htmlFor="userName" className="text-xs font-bold text-cream/40 uppercase tracking-wider block">Full Name</label>
               <input
                  type="text"
                  id="userName"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  disabled={savingSettings}
                  className="block w-full bg-black/20 border border-white/5 rounded-2xl text-cream placeholder-cream/20 focus:border-slate-blue focus:ring-1 focus:ring-slate-blue transition-all py-4 px-6 font-medium shadow-inner"
              />
           </div>

           <div className="space-y-2">
              <label htmlFor="userEmail" className="text-xs font-bold text-cream/40 uppercase tracking-wider block">Email Address</label>
              <input
                  type="email"
                  id="userEmail"
                  value={user?.email || ''}
                  readOnly
                  disabled
                  className="block w-full bg-white/5 border border-transparent rounded-2xl text-cream/40 cursor-not-allowed py-4 px-6 font-medium"
              />
           </div>

           <div className="md:col-span-2 space-y-2">
              <label htmlFor="timeZone" className="text-xs font-bold text-cream/40 uppercase tracking-wider block">Time Zone</label>
              <select
                  id="timeZone"
                  value={currentTimeZone}
                  onChange={(e) => setTimeZone(e.target.value)}
                  disabled={savingSettings}
                  className="block w-full bg-black/20 border border-white/5 rounded-2xl text-cream focus:border-slate-blue focus:ring-1 focus:ring-slate-blue transition-all py-4 px-6 appearance-none font-medium hover:bg-black/30 cursor-pointer"
              >
                  {timezones.map(tz => <option key={tz} value={tz}>{tz}</option>)}
              </select>
           </div>
       </div>

       <div className="pt-6 flex items-center justify-end space-x-4 border-t border-white/5 mt-6">
          {settingsMessage && <span className="text-sm text-pastel-green font-medium animate-fade-in">{settingsMessage}</span>}
          <button
              onClick={handleSavePreferences}
              disabled={savingSettings || loadingSettings}
              className="btn btn-primary px-10 py-4 rounded-full shadow-glow flex items-center gap-2 text-sm uppercase tracking-wider font-bold hover:scale-105 transition-transform"
          >
              {savingSettings ? <FiLoader className="animate-spin"/> : <FiSave />}
              {savingSettings ? 'Saving...' : 'Save Preferences'}
          </button>
       </div>
    </section>
  );

  const renderConnectionsTab = () => (
    <section className="glass-panel p-10 relative overflow-hidden">
        <div className="flex items-center gap-4 mb-8">
            <div className="p-3 bg-slate-blue rounded-2xl shadow-glow text-white">
                <FiGlobe size={24} />
            </div>
            <h2 className="text-2xl font-black text-cream uppercase tracking-tight">Connections</h2>
        </div>
       {connectionError && <p className="text-sm text-soft-coral bg-soft-coral/10 p-4 rounded-2xl border border-soft-coral/20 mb-6 font-medium">{connectionError}</p>}
       <div className="space-y-1">
          {renderConnection('meta', 'Meta (Facebook & Instagram)', <FaFacebook className="text-blue-400" />, false)}
          {renderConnection('linkedin', 'LinkedIn', <FaLinkedin className="text-blue-500" />, false, { onConnect: handleLinkedInConnect, onDisconnect: handleLinkedInDisconnect })}
          {renderConnection('twitter', 'X (Twitter)', <FaTwitter className="text-cream" />, true)}
          {renderConnection('youtube', 'YouTube', <FaYoutube className="text-red-400" />, true)}
          {renderConnection('tiktok', 'TikTok', <FaTiktok className="text-cream" />, true)}
          {renderConnection('pinterest', 'Pinterest', <FaPinterest className="text-red-500" />, true)}
       </div>
    </section>
  );

  const renderNotificationsTab = () => (
    <section className="glass-panel p-10 relative overflow-hidden">
        <div className="flex items-center gap-4 mb-8">
            <div className="p-3 bg-slate-blue rounded-2xl shadow-glow text-white">
                <FiBell size={24} />
            </div>
            <div>
                <h2 className="text-2xl font-black text-cream uppercase tracking-tight">Notifications</h2>
                <p className="text-xs text-cream/30 mt-1">Choose which notifications you receive</p>
            </div>
        </div>

        {/* Master toggles */}
        <div className="space-y-4 mb-6">
            <div className="flex items-center justify-between py-3">
                <div className="flex-1 min-w-0 mr-4">
                    <p className="text-sm font-bold text-cream">In-App Notifications</p>
                    <p className="text-xs text-cream/30 mt-0.5">Show notifications in the bell icon</p>
                </div>
                <button
                    onClick={handleToggleNotificationsMaster}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                        notificationsEnabled ? 'bg-slate-blue' : 'bg-white/10'
                    }`}
                    role="switch"
                    aria-checked={notificationsEnabled}
                >
                    <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-lg transform ring-0 transition duration-200 ease-in-out ${
                        notificationsEnabled ? 'translate-x-5' : 'translate-x-0'
                    }`} />
                </button>
            </div>
            <div className="flex items-center justify-between py-3">
                <div className="flex-1 min-w-0 mr-4">
                    <p className="text-sm font-bold text-cream">Email Notifications</p>
                    <p className="text-xs text-cream/30 mt-0.5">Receive email alerts</p>
                </div>
                <button
                    onClick={handleToggleEmailMaster}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                        emailNotificationsEnabled ? 'bg-slate-blue' : 'bg-white/10'
                    }`}
                    role="switch"
                    aria-checked={emailNotificationsEnabled}
                >
                    <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-lg transform ring-0 transition duration-200 ease-in-out ${
                        emailNotificationsEnabled ? 'translate-x-5' : 'translate-x-0'
                    }`} />
                </button>
            </div>
        </div>

        {/* Separator between master toggles and individual types */}
        <div className="border-t border-white/10 mb-4" />

        {/* Column headers */}
        <div className="flex items-center justify-end gap-6 pb-2 border-b border-white/5 mb-1">
            <span className={`text-[10px] font-bold uppercase tracking-wider w-11 text-center ${notificationsEnabled ? 'text-cream/30' : 'text-cream/15'}`}>In-App</span>
            <span className={`text-[10px] font-bold uppercase tracking-wider w-11 text-center ${emailNotificationsEnabled ? 'text-cream/30' : 'text-cream/15'}`}>Email</span>
        </div>
        <div className="space-y-1">
            {NOTIFICATION_TYPES.map(({ key, label, description, emailOnly }) => (
                <div key={key} className="flex items-center justify-between py-4 border-b border-white/5 last:border-b-0">
                    <div className="flex-1 min-w-0 mr-4">
                        <p className="text-sm font-medium text-cream">{label}</p>
                        <p className="text-xs text-cream/30 mt-0.5">{description}</p>
                    </div>
                    <div className="flex items-center gap-6">
                        {emailOnly ? (
                            <div className="w-11 flex items-center justify-center">
                                <span className="text-[9px] text-cream/20 uppercase tracking-wider">&mdash;</span>
                            </div>
                        ) : (
                            <div className={`${!notificationsEnabled ? 'opacity-30 pointer-events-none' : ''}`}>
                                <button
                                    onClick={() => handleToggleNotification(key)}
                                    disabled={savingNotifPref === key || !notificationsEnabled}
                                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                        notificationPrefs[key] ? 'bg-slate-blue' : 'bg-white/10'
                                    } ${savingNotifPref === key ? 'opacity-50' : ''}`}
                                    role="switch"
                                    aria-checked={notificationPrefs[key]}
                                >
                                    <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-lg transform ring-0 transition duration-200 ease-in-out ${
                                        notificationPrefs[key] ? 'translate-x-5' : 'translate-x-0'
                                    }`} />
                                </button>
                            </div>
                        )}
                        <div className={`${!emailNotificationsEnabled ? 'opacity-30 pointer-events-none' : ''}`}>
                            <button
                                onClick={() => handleToggleEmailPref(key)}
                                disabled={savingEmailPref === key || !emailNotificationsEnabled}
                                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                    emailPrefs[key] ? 'bg-slate-blue' : 'bg-white/10'
                                } ${savingEmailPref === key ? 'opacity-50' : ''}`}
                                role="switch"
                                aria-checked={emailPrefs[key]}
                            >
                                <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-lg transform ring-0 transition duration-200 ease-in-out ${
                                    emailPrefs[key] ? 'translate-x-5' : 'translate-x-0'
                                }`} />
                            </button>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    </section>
  );

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-8 pb-32">
      <h1 className="text-5xl font-black text-cream uppercase tracking-tighter drop-shadow-lg mb-8">
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-slate-blue to-cream">Settings</span>
      </h1>

      {/* Tab Bar */}
      <div className="flex flex-wrap gap-2 mb-8 p-1.5 bg-warm-gray/10 backdrop-blur-md rounded-2xl border border-white/5">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-bold uppercase tracking-wider transition-all ${
              activeTab === id
                ? 'bg-slate-blue text-white shadow-glow'
                : 'text-cream/40 hover:text-cream/70 hover:bg-white/5'
            }`}
          >
            <Icon size={16} />
            <span className="hidden sm:inline">{label}</span>
          </button>
        ))}
      </div>

      {(loadingSettings || isLoadingConnections) && !settingsError && !connectionError && (
          <div className="text-center p-12 bg-warm-gray/10 backdrop-blur-md rounded-3xl border border-white/5">
             <FiLoader className="animate-spin text-3xl text-slate-blue mx-auto mb-4" />
             <p className="text-cream/40 text-sm font-bold tracking-widest uppercase">Loading Preferences...</p>
          </div>
       )}

       {!loadingSettings && !isLoadingConnections && (
          <div className="space-y-8">
              {activeTab === 'account' && renderAccountTab()}
              {activeTab === 'connections' && renderConnectionsTab()}
              {activeTab === 'team' && <TeamManagement userId={userId} userData={userData} />}
              {activeTab === 'notifications' && renderNotificationsTab()}

              {/* Logout Button — always visible */}
              <section className="pt-4 text-center sm:text-left">
                <button
                  onClick={onLogout}
                  className="inline-flex items-center justify-center py-4 px-8 border border-soft-coral/30 shadow-sm text-sm font-bold text-soft-coral bg-soft-coral/5 hover:bg-soft-coral/10 rounded-full transition-all uppercase tracking-widest hover:scale-105"
                >
                  <FiPower className="mr-2"/> Log Out
                </button>
              </section>
          </div>
      )}

      <MetaConsentModal
        isOpen={showMetaConsent}
        onClose={() => setShowMetaConsent(false)}
        onConfirm={() => { setShowMetaConsent(false); handleMetaConnect(); }}
        isLoading={isLoadingConnections}
      />
    </div>
  );
}

export default Settings;
